import { environmentHttpClient } from "../httpClient";
import {
	IntegrationFlowHistoryModel,
	IntegrationFlowKind
} from "./integrationBuilderFlows";

export type IntegrationAppOverviewListItemModel = {
	id: number;
	name: string;
	flowCount: number;
	isDefault: boolean;
};

export type IntegrationAppsOverviewPageViewModel = {
	apps: readonly IntegrationAppOverviewListItemModel[];
};

export function getPageViewModel(
	signal?: AbortSignal
): Promise<IntegrationAppsOverviewPageViewModel> {
	return environmentHttpClient.get<IntegrationAppsOverviewPageViewModel>(
		"UiApi/IntegrationBuilder/Apps",
		undefined,
		{ signal }
	);
}

export type IntegrationAppSaveModel = {
	name: string;
};

export function createApp(
	model: IntegrationAppSaveModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		"UiApi/IntegrationBuilder/Apps",
		model,
		{ signal }
	);
}

export function updateApp(
	appId: number,
	model: IntegrationAppSaveModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		`UiApi/IntegrationBuilder/Apps/${appId}`,
		model,
		{ signal }
	);
}

export function deleteApp(
	appId: number,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.delete<void>(
		`UiApi/IntegrationBuilder/Apps/${appId}`,
		undefined,
		{ signal }
	);
}

export type IntegrationAppOptionModel = {
	id: number;
	name: string;
};

export type DefaultAppDialogViewModel = {
	apps: readonly IntegrationAppOptionModel[];
	defaultAppId: number | null;
};

export function getDefaultAppDialogViewModel(
	signal?: AbortSignal
): Promise<DefaultAppDialogViewModel> {
	return environmentHttpClient.get<DefaultAppDialogViewModel>(
		"UiApi/IntegrationBuilder/Apps/Default",
		undefined,
		{ signal }
	);
}

export type IntegrationAppSetDefaultModel = {
	appId: number;
};

export function setDefaultApp(
	model: IntegrationAppSetDefaultModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		"UiApi/IntegrationBuilder/Apps/SetDefault",
		model,
		{ signal }
	);
}

export function clearDefaultApp(
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		"UiApi/IntegrationBuilder/Apps/ClearDefault",
		undefined,
		{ signal }
	);
}

export type IntegrationSharedSettingOptionModel = {
	id: number;
	value: string;
	displayName: string;
	description: string | null;
};

export type IntegrationSharedSettingViewModel = {
	id: number;
	value: string;
	displayName: string;
	description: string | null;
	isUsedByFlows: boolean;
};

export type IntegrationAppViewModel = {
	id: number;
	name: string;
	flows: readonly IntegrationFlowHistoryModel[];
	sharedSettings: readonly IntegrationSharedSettingViewModel[];
	creatableKinds: readonly IntegrationFlowKind[];
};

export function getApp(
	appId: number,
	signal?: AbortSignal
): Promise<IntegrationAppViewModel> {
	return environmentHttpClient.get<IntegrationAppViewModel>(
		`UiApi/IntegrationBuilder/Apps/${appId}`,
		undefined,
		{ signal }
	);
}

export type IntegrationSharedSettingSaveModel = {
	value: string;
	displayName: string;
	description: string;
};

export function createSharedSetting(
	appId: number,
	model: IntegrationSharedSettingSaveModel,
	signal?: AbortSignal
): Promise<number> {
	return environmentHttpClient.post<number>(
		`UiApi/IntegrationBuilder/Apps/${appId}/SharedSetting`,
		model,
		{ signal }
	);
}

export function updateSharedSetting(
	sharedSettingId: number,
	model: IntegrationSharedSettingSaveModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		`UiApi/IntegrationBuilder/SharedSettings/${sharedSettingId}`,
		model,
		{ signal }
	);
}

export enum IntegrationSharedSettingDeleteResultKind {
	DeletedSuccessfully = 1,
	SharedSettingIsInUse = 2
}

export type IntegrationSharedSettingDeleteResult =
	| {
		kind: IntegrationSharedSettingDeleteResultKind.DeletedSuccessfully;
	}
	| {
		kind: IntegrationSharedSettingDeleteResultKind.SharedSettingIsInUse;
		sharedSettingInUseIds: readonly number[];
	};

export function deleteSharedSettings(
	sharedSettingIds: readonly number[],
	signal?: AbortSignal
): Promise<IntegrationSharedSettingDeleteResult> {
	return environmentHttpClient.delete<IntegrationSharedSettingDeleteResult>(
		`UiApi/IntegrationBuilder/SharedSettings`,
		sharedSettingIds,
		{ signal }
	);
}
